@if (isDebugMode) {
  <div
    class="debug-footer-panel"
    [class.debug-footer-panel--open]="debugPanelOpen"
  >
    <!-- ── Toggle bar (always visible) ─────────────────────── -->
    <div
      class="debug-footer-bar"
      role="button"
      tabindex="0"
      (click)="togglePanel()"
      (keyup.enter)="togglePanel()"
      (keyup.space)="togglePanel()"
    >
      <span class="badge bg-danger me-2" style="font-size: 12px">🔍 DEBUG</span>
      <strong class="text-white small">Quote Debug Info</strong>
      @if (debugLoading) {
        <span class="ms-2 text-warning small">Loading…</span>
      }
      @if (!debugLoading && debugData) {
        <span class="ms-2 text-success small">✓ data loaded</span>
      }
      <span class="ms-auto text-white-50 small">{{
        debugPanelOpen ? "▼ collapse" : "▲ expand"
      }}</span>
    </div>
    <!-- ── Scrollable body (only when open) ─────────────────── -->
    @if (debugPanelOpen) {
      <div class="debug-footer-body">
        @if (debugLoading) {
          <div class="text-center py-3">
            <app-spinner [size]="30"></app-spinner>
            <span class="ms-2 text-muted">Loading debug data…</span>
          </div>
        }
        @if (!debugLoading && debugData) {
          <!-- ─── Quote Info ─────────────────────────────────── -->
          @if (debugData.quoteInfo) {
            <div class="card mb-3 border-info">
              <div
                class="card-header bg-info bg-opacity-10 d-flex align-items-center gap-2"
              >
                <strong>📊 Quote Info</strong>
              </div>
              <div class="card-body p-2 small">
                <div class="row row-cols-2 row-cols-lg-4 g-2">
                  <div class="col">
                    <small class="text-muted d-block">Type</small>
                    <code>{{ debugData.quoteInfo.type || "—" }}</code>
                  </div>
                  <div class="col">
                    <small class="text-muted d-block"
                      >Organisation spread</small
                    >
                    <strong
                      >{{
                        debugData.quoteInfo.spread !== null
                          ? debugData.quoteInfo.spread
                          : "—"
                      }}
                      %</strong
                    >
                  </div>
                  <div class="col">
                    <small class="text-muted d-block"
                      >Organisation currency</small
                    >
                    <code>{{
                      debugData.quoteInfo.organizationCurrency || "—"
                    }}</code>
                  </div>
                  @if (debugData.quoteInfo.totalCurrency) {
                    <div class="col">
                      <small class="text-muted d-block">Total currency</small>
                      <code>{{ debugData.quoteInfo.totalCurrency }}</code>
                    </div>
                  }
                  @if (debugData.quoteInfo.grandTotalCurrency) {
                    <div class="col">
                      <small class="text-muted d-block"
                        >Grand total currency</small
                      >
                      <code>{{ debugData.quoteInfo.grandTotalCurrency }}</code>
                    </div>
                  }
                  @if (debugData.quoteInfo.showInsurance) {
                    <div class="col">
                      <small class="text-muted d-block">Insurance total</small>
                      <strong
                        >{{
                          debugData.quoteInfo.insuranceTotal | number: "1.2-2"
                        }}
                        {{ debugData.quoteInfo.insuranceCurrency }}</strong
                      >
                    </div>
                  }
                </div>
              </div>
            </div>
          }
          <!-- ─── Exchange Rates ───────────────────────────── -->
          @if (debugData.exchangeRates?.length) {
            <div class="card mb-3 border-success">
              <div
                class="card-header bg-success bg-opacity-10 d-flex align-items-center gap-2"
              >
                <strong>💱 Exchange Rates Used in Total Calculation</strong>
                <span class="badge bg-secondary ms-auto"
                  >{{ debugData.exchangeRates.length }} pair(s)</span
                >
              </div>
              <div class="card-body p-2 small">
                @for (er of debugData.exchangeRates; track er.label) {
                  <div
                    class="d-flex flex-wrap align-items-center gap-3 mb-2 p-2 rounded"
                    style="background: #f0fff4; border: 1px solid #b7ebc5"
                  >
                    <span class="badge bg-success fs-6 px-3 py-2">{{
                      er.label
                    }}</span>
                    <span class="text-muted"
                      >Spread: <strong>{{ er.spread }} %</strong></span
                    >
                    <code class="small">{{ er.formula }}</code>
                  </div>
                }
              </div>
            </div>
          }
          <!-- ─── Rate Calculations ────────────────────────── -->
          @if (debugData.rates?.length) {
            <div class="card mb-3 border-primary">
              <div
                class="card-header bg-primary bg-opacity-10 d-flex align-items-center gap-2"
              >
                <strong>🚚 Rate Calculations</strong>
                <span class="badge bg-secondary ms-auto"
                  >{{ debugData.rates.length }} rate(s)</span
                >
              </div>
              <div class="card-body p-2">
                @for (r of debugData.rates; track r.id) {
                  <div
                    class="mb-3 border rounded p-2"
                    [class.border-success]="r.hasRoute"
                    [class.border-light]="!r.hasRoute"
                  >
                    <!-- Rate header -->
                    <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <strong class="small">{{ r.name }}</strong>
                      @if (r.carrierName) {
                        <span class="badge bg-light text-dark border">{{
                          r.carrierName
                        }}</span>
                      }
                      <code class="small">{{ r.currency }}</code>
                      @if (r.type) {
                        <span class="badge bg-info text-dark">{{
                          r.type | uppercase
                        }}</span>
                      }
                      @if (r.hasRoute) {
                        <span class="badge bg-success">Selected</span>
                      } @else {
                        <span class="badge bg-secondary">Not selected</span>
                      }
                      @if (r.validFrom || r.validTill) {
                        <small class="text-muted ms-auto"
                          >{{ r.validFrom }} – {{ r.validTill }}</small
                        >
                      }
                      @if (r.id) {
                        <a
                          pButton
                          severity="secondary"
                          [outlined]="true"
                          size="small"
                          [href]="'/rate/ltl/flat/' + r.id"
                          target="_blank"
                          style="font-size: 11px; padding: 1px 6px"
                          >#{{ r.id }} ↗</a
                        >
                      }
                    </div>
                    <!-- LTL pricing breakdown -->
                    @if (r.type === "ltl") {
                      <div class="row row-cols-2 row-cols-lg-4 g-2 mb-2 small">
                        <div class="col">
                          <small class="text-muted d-block"
                            >Chargeable weight</small
                          >
                          <strong
                            >{{
                              r.chargeableWeight | number: "1.2-2"
                            }}
                            kg</strong
                          >
                        </div>
                        <div class="col">
                          <small class="text-muted d-block"
                            >Cost (CW price)</small
                          >
                          <strong>{{
                            r.chargeableWeightPrice | number: "1.2-2"
                          }}</strong>
                        </div>
                        <div class="col">
                          <small class="text-muted d-block">Margin</small>
                          <strong>{{ r.margin | number: "1.2-2" }} %</strong>
                        </div>
                        <div class="col">
                          <small class="text-muted d-block">Total price</small>
                          <strong class="text-primary"
                            >{{ r.totalPrice | number: "1.0-0" }}
                            {{ r.currency }}</strong
                          >
                        </div>
                        <div class="col">
                          <small class="text-muted d-block"
                            >Freight price</small
                          >
                          <strong>{{
                            r.freightPrice | number: "1.2-2"
                          }}</strong>
                        </div>
                        @if (r.noMarginPrice) {
                          <div class="col">
                            <small class="text-muted d-block"
                              >No-margin surcharges</small
                            >
                            <strong>{{
                              r.noMarginPrice | number: "1.2-2"
                            }}</strong>
                          </div>
                        }
                        @if (
                          r.flatRateRouteId ||
                          r.pickupRateRouteId ||
                          r.deliveryRateRouteId
                        ) {
                          <div class="col-12">
                            <small class="text-muted"
                              >Route IDs: flat=<code>{{
                                r.flatRateRouteId ?? "—"
                              }}</code>
                              pickup=<code>{{
                                r.pickupRateRouteId ?? "—"
                              }}</code>
                              delivery=<code>{{
                                r.deliveryRateRouteId ?? "—"
                              }}</code></small
                            >
                          </div>
                        }
                      </div>
                      @if (r.rateSurcharges?.length) {
                        <div class="mb-2 small">
                          <strong class="text-muted">Rate surcharges: </strong>
                          @for (s of r.rateSurcharges; track $index) {
                            <span class="badge bg-light text-dark border me-1"
                              >{{ s.name }}:
                              {{ s.price | number: "1.2-2" }}</span
                            >
                          }
                        </div>
                      }
                      @if (r.noMarginSurcharges?.length) {
                        <div class="mb-2 small">
                          <strong class="text-muted"
                            >No-margin surcharges:
                          </strong>
                          @for (s of r.noMarginSurcharges; track $index) {
                            <span class="badge bg-secondary me-1"
                              >{{ s.name }}:
                              {{ s.price | number: "1.2-2" }}</span
                            >
                          }
                        </div>
                      }
                    }
                    <!-- FCL pricing: containers + surcharge breakdown -->
                    @if (r.type === "fcl") {
                      @if (
                        r.surchargeBreakdown?.length ||
                        r.freightContainers?.length
                      ) {
                        <div class="mb-2 small" style="overflow-x: auto">
                          <table
                            class="table table-sm table-bordered mb-0"
                            style="min-width: 300px"
                          >
                            <thead class="table-secondary">
                              <tr>
                                <th>Surcharge</th>
                                @for (c of r.containers; track c.name) {
                                  <th class="text-end">
                                    {{ c.name }} ({{ r.currency }})
                                  </th>
                                }
                                @for (
                                  toCur of relevantConvertedCurrencies(
                                    r.currency
                                  );
                                  track toCur
                                ) {
                                  @for (c of r.containers; track c.name) {
                                    <th class="text-end">
                                      {{ c.name }} ({{ toCur }})
                                    </th>
                                  }
                                }
                              </tr>
                            </thead>
                            <tbody>
                              @if (r.freightContainers?.length) {
                                <tr class="table-light">
                                  <td><strong>Freight</strong></td>
                                  @for (c of r.containers; track c.name) {
                                    <td class="text-end">
                                      <strong>{{
                                        containerVal(
                                          r.freightContainers,
                                          c.name
                                        ) | number: "1.0-0"
                                      }}</strong>
                                    </td>
                                  }
                                  @for (
                                    toCur of relevantConvertedCurrencies(
                                      r.currency
                                    );
                                    track toCur
                                  ) {
                                    @for (c of r.containers; track c.name) {
                                      @let raw =
                                        containerVal(
                                          r.freightContainers,
                                          c.name
                                        );
                                      <td class="text-end">
                                        @if (raw !== null) {
                                          {{
                                            convertedVal(raw, r.currency, toCur)
                                              | number: "1.0-0"
                                          }}
                                        }
                                      </td>
                                    }
                                  }
                                </tr>
                              }
                              @for (s of r.surchargeBreakdown; track s.name) {
                                <tr>
                                  <td>{{ s.name }}</td>
                                  @for (c of r.containers; track c.name) {
                                    @let val =
                                      containerVal(s.containers, c.name);
                                    <td class="text-end">
                                      {{
                                        val !== null
                                          ? (val | number: "1.0-0")
                                          : ""
                                      }}
                                    </td>
                                  }
                                  @for (
                                    toCur of relevantConvertedCurrencies(
                                      r.currency
                                    );
                                    track toCur
                                  ) {
                                    @for (c of r.containers; track c.name) {
                                      @let raw =
                                        containerVal(s.containers, c.name);
                                      <td class="text-end">
                                        @if (raw !== null && raw !== 0) {
                                          {{
                                            convertedVal(raw, r.currency, toCur)
                                              | number: "1.0-0"
                                          }}
                                        }
                                      </td>
                                    }
                                  }
                                </tr>
                              }
                              <tr class="table-primary fw-bold">
                                <td>Total</td>
                                @for (c of r.containers; track c.name) {
                                  <td class="text-end">
                                    {{ c.value | number: "1.0-0" }}
                                  </td>
                                }
                                @for (
                                  toCur of relevantConvertedCurrencies(
                                    r.currency
                                  );
                                  track toCur
                                ) {
                                  @for (c of r.containers; track c.name) {
                                    <td class="text-end">
                                      {{
                                        convertedVal(c.value, r.currency, toCur)
                                          | number: "1.0-0"
                                      }}
                                    </td>
                                  }
                                }
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      } @else {
                        <div class="mb-2 small">
                          <strong class="text-muted">Container prices: </strong>
                          @for (c of r.containers; track c.name) {
                            <span class="badge bg-light text-dark border me-1"
                              >{{ c.name }}:
                              <strong>{{ c.value | number: "1.0-0" }}</strong>
                              {{ r.currency }}</span
                            >
                          }
                        </div>
                      }
                    }
                    <!-- LCL pricing -->
                    @if (r.type === "lcl") {
                      <div class="row row-cols-2 row-cols-lg-4 g-2 mb-2 small">
                        @if (r.weightPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block"
                              >Weight price</small
                            ><strong>{{
                              r.weightPrice | number: "1.2-2"
                            }}</strong>
                          </div>
                        }
                        @if (r.measurementPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block"
                              >Measurement price</small
                            ><strong>{{
                              r.measurementPrice | number: "1.2-2"
                            }}</strong>
                          </div>
                        }
                        @if (r.blPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block">BL price</small
                            ><strong>{{ r.blPrice | number: "1.2-2" }}</strong>
                          </div>
                        }
                        @if (r.minPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block">Min price</small
                            ><strong>{{ r.minPrice | number: "1.2-2" }}</strong>
                          </div>
                        }
                        @if (r.chargeableWeight) {
                          <div class="col">
                            <small class="text-muted d-block"
                              >Chargeable weight</small
                            ><strong
                              >{{
                                r.chargeableWeight | number: "1.2-2"
                              }}
                              kg</strong
                            >
                          </div>
                        }
                        @if (r.chargeableWeightPrice) {
                          <div class="col">
                            <small class="text-muted d-block">CW price</small
                            ><strong class="text-primary"
                              >{{ r.chargeableWeightPrice | number: "1.2-2" }}
                              {{ r.currency }}</strong
                            >
                          </div>
                        }
                      </div>
                    }
                    <!-- PTL pricing -->
                    @if (r.type === "ptl") {
                      <div class="row row-cols-2 row-cols-lg-4 g-2 mb-2 small">
                        @if (r.blPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block">BL price</small
                            ><strong>{{ r.blPrice | number: "1.2-2" }}</strong>
                          </div>
                        }
                        @if (r.chargeableWeightPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block">Margin</small
                            ><strong>{{
                              r.chargeableWeightPrice | number: "1.2-2"
                            }}</strong>
                          </div>
                        }
                        @if (r.totalPrice !== null) {
                          <div class="col">
                            <small class="text-muted d-block">Total price</small
                            ><strong class="text-primary"
                              >{{ r.totalPrice | number: "1.2-2" }}
                              {{ r.currency }}</strong
                            >
                          </div>
                        }
                      </div>
                    }
                    <!-- Debug statistics -->
                    @if (r.debugStatistics?.length) {
                      <details class="mb-2">
                        <summary
                          class="small text-muted"
                          style="cursor: pointer"
                        >
                          Debug statistics ({{ r.debugStatistics.length }})
                        </summary>
                        <table
                          class="table table-sm table-bordered table-striped mt-1 small"
                        >
                          <thead class="table-secondary">
                            <tr>
                              <th>Code</th>
                              <th>Rate name</th>
                              <th>Markup %</th>
                              <th>Price</th>
                              <th>Currency</th>
                              <th>No margin</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (ds of r.debugStatistics; track $index) {
                              <tr>
                                <td>
                                  <code>{{ ds.code }}</code>
                                </td>
                                <td>{{ ds.rateName }}</td>
                                <td>{{ ds.markup | number: "1.2-2" }}</td>
                                <td>{{ ds.price | number: "1.2-2" }}</td>
                                <td>
                                  <code>{{ ds.currency }}</code>
                                </td>
                                <td>{{ ds.isNoMargin }}</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </details>
                    }
                    <!-- debugInfo HTML with resolved links -->
                    @if (r.debugInfo) {
                      <div
                        class="p-2 rounded small"
                        style="
                          background: #f0f4ff;
                          border: 1px solid #c8d8ff;
                          font-family: monospace;
                          white-space: pre-wrap;
                          line-height: 1.7;
                        "
                        [innerHTML]="formatDebugString(r.debugInfo)"
                      ></div>
                    }
                  </div>
                }
              </div>
            </div>
          }
          <!-- ─── Location Surcharges Debug ─────────────────── -->
          @if (debugData.locationSurcharges?.length) {
            <div class="card mb-3 border-warning">
              <div
                class="card-header bg-warning bg-opacity-10 d-flex align-items-center gap-2"
              >
                <strong>🏭 Local Charges Calculation Log</strong>
                <span class="badge bg-secondary ms-auto"
                  >{{ debugData.locationSurcharges.length }} charge(s)</span
                >
              </div>
              <div class="card-body p-2">
                @for (ls of debugData.locationSurcharges; track ls.id) {
                  <div class="mb-3 border rounded p-2">
                    <div class="d-flex align-items-center gap-2 mb-2">
                      <strong class="small">{{ ls.name || "—" }}</strong>
                      @if (ls.location) {
                        <span class="badge bg-light text-dark border">{{
                          ls.location
                        }}</span>
                      }
                      @if (ls.currency) {
                        <code class="small">{{ ls.currency }}</code>
                      }
                      <span class="badge bg-secondary ms-1 small">{{
                        ls.chargedFor
                      }}</span>
                      <span
                        class="badge bg-light text-muted border ms-1 small"
                        >{{ ls.assignment }}</span
                      >
                      <span class="ms-auto d-flex gap-1">
                        @if (ls.locationId) {
                          <a
                            pButton
                            severity="secondary"
                            [outlined]="true"
                            size="small"
                            [href]="'/location/' + ls.locationId"
                            target="_blank"
                            style="font-size: 11px; padding: 1px 6px"
                            title="Open location"
                            >location #{{ ls.locationId }} ↗</a
                          >
                        }
                      </span>
                    </div>
                    @if (ls.containers?.length) {
                      <div class="small mb-2" style="overflow-x: auto">
                        <table
                          class="table table-sm table-bordered mb-0"
                          style="min-width: 200px"
                        >
                          <thead class="table-secondary">
                            <tr>
                              @for (c of ls.containers; track c.name) {
                                <th class="text-end">
                                  {{ c.name }} ({{ ls.currency }})
                                </th>
                              }
                              @for (
                                toCur of relevantConvertedCurrencies(
                                  ls.currency
                                );
                                track toCur
                              ) {
                                @for (c of ls.containers; track c.name) {
                                  <th class="text-end">
                                    {{ c.name }} ({{ toCur }})
                                  </th>
                                }
                              }
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              @for (c of ls.containers; track c.name) {
                                <td class="text-end fw-bold">
                                  {{ c.value | number: "1.0-0" }}
                                </td>
                              }
                              @for (
                                toCur of relevantConvertedCurrencies(
                                  ls.currency
                                );
                                track toCur
                              ) {
                                @for (c of ls.containers; track c.name) {
                                  <td class="text-end">
                                    {{
                                      convertedVal(c.value, ls.currency, toCur)
                                        | number: "1.0-0"
                                    }}
                                  </td>
                                }
                              }
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    }
                    @if (
                      ls.totalShipmentPrice !== null &&
                      ls.totalShipmentPrice !== 0
                    ) {
                      <div class="small mb-2">
                        <span class="text-muted">Shipment price: </span>
                        <strong
                          >{{ ls.totalShipmentPrice | number: "1.0-2" }}
                          {{ ls.currency }}</strong
                        >
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
          <!-- ─── Estimated Transport Cost ──── -->
          @let est = computeEstimatedTotal();
          @if (est) {
            <div class="card mb-3 border-success">
              <div
                class="card-header bg-success bg-opacity-10 d-flex align-items-center gap-2"
              >
                <strong>🧮 Estimated Transport Cost - Step by Step</strong>
              </div>
              <div class="card-body p-2 small" style="overflow-x: auto">
                <table class="table table-sm table-bordered mb-0">
                  <thead class="table-secondary">
                    <tr>
                      <th>Component</th>
                      <th>Currency</th>
                      @for (cn of est.containers; track cn) {
                        <th class="text-end">{{ cn }}</th>
                      }
                      @if (debugData.quoteInfo.totalCurrency) {
                        @for (cn of est.containers; track cn) {
                          <th class="text-end">
                            {{ cn }} ({{ debugData.quoteInfo.totalCurrency }})
                          </th>
                        }
                      }
                    </tr>
                  </thead>
                  <tbody>
                    @for (group of est.groups; track $index) {
                      <tr class="table-light">
                        <td>{{ group.rateStep.label }}</td>
                        <td>
                          <code>{{ group.rateStep.currency }}</code>
                        </td>
                        @for (v of group.rateStep.values; track v.name) {
                          <td class="text-end">
                            {{ v.raw | number: "1.0-0" }}
                          </td>
                        }
                        @for (v of group.rateStep.values; track v.name) {
                          <td class="text-end">
                            @if (
                              group.rateStep.currency ===
                              debugData.quoteInfo.totalCurrency
                            ) {
                              {{ v.raw | number: "1.0-0" }}
                            } @else {
                              {{ v.converted | number: "1.0-0" }}
                            }
                          </td>
                        }
                      </tr>
                      @for (step of group.surchargeSteps; track $index) {
                        <tr class="table-warning">
                          <td class="ps-3">{{ step.label }}</td>
                          <td>
                            <code>{{ step.currency }}</code>
                          </td>
                          @for (v of step.values; track v.name) {
                            <td class="text-end">
                              {{ v.raw | number: "1.0-0" }}
                            </td>
                          }
                          @for (v of step.values; track v.name) {
                            <td class="text-end">
                              @if (
                                step.currency ===
                                debugData.quoteInfo.totalCurrency
                              ) {
                                {{ v.raw | number: "1.0-0" }}
                              } @else {
                                {{ v.converted | number: "1.0-0" }}
                              }
                            </td>
                          }
                        </tr>
                      }
                      <tr class="table-success fw-bold">
                        <td colspan="2">
                          <strong
                            >Total {{ group.rateStep.label }} ({{
                              debugData.quoteInfo.totalCurrency
                            }})</strong
                          >
                        </td>
                        @for (cn of est.containers; track cn) {
                          <td class="text-end text-muted">—</td>
                        }
                        @for (t of group.subtotals; track t.name) {
                          <td class="text-end">
                            <strong>{{ t.value | number: "1.0-0" }}</strong>
                          </td>
                        }
                      </tr>
                      @if (!$last) {
                        <tr>
                          <td
                            [attr.colspan]="2 + est.containers.length * 2"
                            style="
                              height: 4px;
                              background: #e9ecef;
                              border: none;
                            "
                          ></td>
                        </tr>
                      }
                    }
                  </tbody>
                </table>
              </div>
            </div>
          }
          <!-- ─── Margin Machine Rules ───────────────────────── -->
          <div class="card mb-3 border-secondary">
            <div class="card-header bg-light d-flex align-items-center gap-2">
              <strong>⚙️ Margin Machine — Applied Rules</strong>
              <span class="badge bg-secondary ms-auto"
                >{{
                  debugData.marginRuleQuoteRates?.length || 0
                }}
                record(s)</span
              >
            </div>
            <div class="card-body p-2">
              @if (!debugData.marginRuleQuoteRates?.length) {
                <div class="alert alert-warning mb-0">
                  No margin-rule records found for this quote.
                </div>
              }
              @for (entry of debugData.marginRuleQuoteRates; track entry.id) {
                <div class="card mb-3 border-secondary">
                  <div
                    class="card-header d-flex align-items-center gap-2 bg-light"
                  >
                    <strong class="me-1">Rule #{{ entry.marginRuleId }}</strong>
                    <span class="badge bg-secondary"
                      >v{{ entry.version ?? entry.marginRuleVersion }}</span
                    >
                    <span class="flex-grow-1">{{ entry.name }}</span>
                    <span class="badge bg-primary"
                      >Score: {{ entry.score }}</span
                    >
                    <a
                      pButton
                      severity="secondary"
                      [outlined]="true"
                      size="small"
                      class="ms-2"
                      [href]="'/margin-rule/rule-quote-rate/' + entry.id"
                      target="_blank"
                      style="font-size: 11px; padding: 1px 6px"
                      title="Open rule-quote-rate detail"
                      >#{{ entry.id }} ↗</a
                    >
                  </div>
                  <div class="card-body p-2">
                    <div class="row row-cols-2 row-cols-lg-4 g-2 mb-2">
                      <div class="col">
                        <small class="text-muted d-block"
                          >Price before → after</small
                        >
                        <strong>{{
                          entry.priceBefore !== null
                            ? (entry.priceBefore | number: "1.2-2")
                            : "—"
                        }}</strong>
                        <span class="mx-1">→</span>
                        <strong class="text-success">{{
                          entry.priceAfter !== null
                            ? (entry.priceAfter | number: "1.2-2")
                            : "—"
                        }}</strong>
                      </div>
                      <div class="col">
                        <small class="text-muted d-block"
                          >Margin before → after</small
                        >
                        <strong>{{
                          entry.marginBefore !== null
                            ? (entry.marginBefore | number: "1.2-2")
                            : "—"
                        }}</strong>
                        <span class="mx-1">→</span>
                        <strong class="text-success">{{
                          entry.marginAfter !== null
                            ? (entry.marginAfter | number: "1.2-2")
                            : "—"
                        }}</strong>
                      </div>
                      <div class="col">
                        <small class="text-muted d-block">Margin rule</small>
                        <strong>{{ entry.margin }}</strong>
                        <span class="badge bg-info text-dark ms-1">{{
                          entry.marginType
                        }}</span>
                        <span class="badge bg-secondary ms-1">{{
                          entry.applicability
                        }}</span>
                      </div>
                      <div class="col">
                        <small class="text-muted d-block">Applied at</small>
                        {{
                          entry.appliedAt
                            ? (entry.appliedAt | date: "yyyy-MM-dd HH:mm:ss")
                            : "—"
                        }}
                        @if (
                          entry.wageOverride !== null &&
                          entry.wageOverride !== undefined
                        ) {
                          <br /><small class="text-muted"
                            >Wage override:
                            <strong>{{ entry.wageOverride }}</strong></small
                          >
                        }
                      </div>
                    </div>
                    <div class="mb-2 p-2 rounded bg-light small">
                      <strong class="text-muted me-2">Source IDs</strong>
                      Rate: <code>{{ entry.rate || "—" }}</code> &nbsp;|&nbsp;
                      Pickup route:
                      <code>{{ entry.pickupRateRoute || "—" }}</code>
                      &nbsp;|&nbsp; Flat route:
                      <code>{{ entry.flatRateRoute || "—" }}</code>
                      &nbsp;|&nbsp; Delivery route:
                      <code>{{ entry.deliveryRateRoute || "—" }}</code>
                    </div>
                    @if (
                      entry.clientCountry ||
                      entry.daysMask ||
                      entry.timeOn ||
                      entry.timeOff ||
                      entry.dateTimeOn ||
                      entry.dateTimeOff ||
                      entry.pickupDateMin ||
                      entry.pickupDateMax ||
                      entry.postalCodeMinOrigin ||
                      entry.postalCodeMaxOrigin ||
                      entry.postalCodeMinDestination ||
                      entry.postalCodeMaxDestination ||
                      entry.colliWeight ||
                      entry.colliLength ||
                      entry.colliWidth ||
                      entry.colliHeight ||
                      entry.calculatedPrice
                    ) {
                      <div
                        class="mb-2 p-2 rounded small"
                        style="background: #f8f9fa; border: 1px solid #dee2e6"
                      >
                        <strong class="text-muted me-2">Conditions</strong>
                        @if (entry.clientCountry) {
                          <span class="me-2"
                            >Country:
                            <strong>{{ entry.clientCountry }}</strong></span
                          >
                        }
                        @if (entry.daysMask) {
                          <span class="me-2"
                            >Days mask:
                            <strong>{{ entry.daysMask }}</strong></span
                          >
                        }
                        @if (entry.timeOn || entry.timeOff) {
                          <span class="me-2"
                            >Time: <strong>{{ entry.timeOn ?? "—" }}</strong> –
                            <strong>{{ entry.timeOff ?? "—" }}</strong></span
                          >
                        }
                        @if (entry.dateTimeOn || entry.dateTimeOff) {
                          <span class="me-2"
                            >Date range:
                            <strong>{{ entry.dateTimeOn ?? "—" }}</strong> –
                            <strong>{{
                              entry.dateTimeOff ?? "—"
                            }}</strong></span
                          >
                        }
                        @if (entry.pickupDateMin || entry.pickupDateMax) {
                          <span class="me-2"
                            >Pickup date:
                            <strong>{{ entry.pickupDateMin ?? "—" }}</strong> –
                            <strong>{{
                              entry.pickupDateMax ?? "—"
                            }}</strong></span
                          >
                        }
                        @if (
                          entry.postalCodeMinOrigin || entry.postalCodeMaxOrigin
                        ) {
                          <span class="me-2"
                            >ZIP origin:
                            <strong>{{
                              entry.postalCodeMinOrigin ?? "—"
                            }}</strong>
                            –
                            <strong>{{
                              entry.postalCodeMaxOrigin ?? "—"
                            }}</strong></span
                          >
                        }
                        @if (
                          entry.postalCodeMinDestination ||
                          entry.postalCodeMaxDestination
                        ) {
                          <span class="me-2"
                            >ZIP dest:
                            <strong>{{
                              entry.postalCodeMinDestination ?? "—"
                            }}</strong>
                            –
                            <strong>{{
                              entry.postalCodeMaxDestination ?? "—"
                            }}</strong></span
                          >
                        }
                        @if (entry.colliWeight) {
                          <span class="me-2"
                            >Weight
                            <strong
                              >{{ entry.colliWeightOperator }}
                              {{ entry.colliWeight }}</strong
                            ></span
                          >
                        }
                        @if (entry.colliLength) {
                          <span class="me-2"
                            >Length
                            <strong
                              >{{ entry.colliLengthOperator }}
                              {{ entry.colliLength }}</strong
                            ></span
                          >
                        }
                        @if (entry.colliWidth) {
                          <span class="me-2"
                            >Width
                            <strong
                              >{{ entry.colliWidthOperator }}
                              {{ entry.colliWidth }}</strong
                            ></span
                          >
                        }
                        @if (entry.colliHeight) {
                          <span class="me-2"
                            >Height
                            <strong
                              >{{ entry.colliHeightOperator }}
                              {{ entry.colliHeight }}</strong
                            ></span
                          >
                        }
                        @if (entry.calculatedPrice) {
                          <span class="me-2"
                            >Calculated price
                            <strong
                              >{{ entry.calculatedPriceOperator }}
                              {{ entry.calculatedPrice }}</strong
                            ></span
                          >
                        }
                      </div>
                    }
                    @if (
                      entry.services?.length ||
                      entry.originCountries?.length ||
                      entry.destinationCountries?.length ||
                      entry.originLocations?.length ||
                      entry.destinationLocations?.length
                    ) {
                      <div
                        class="mb-2 p-2 rounded small"
                        style="background: #f0f4ff; border: 1px solid #c8d8ff"
                      >
                        <strong class="text-muted me-2">Applied filters</strong>
                        @if (entry.services?.length) {
                          <span class="me-3"
                            >Services:
                            @for (s of entry.services; track s.id) {
                              <span class="badge bg-light text-dark border me-1"
                                >{{ s.name }}
                                <small class="text-muted"
                                  >#{{ s.id }}</small
                                ></span
                              >
                            }
                          </span>
                        }
                        @if (entry.originCountries?.length) {
                          <span class="me-3"
                            >Origin:
                            @for (c of entry.originCountries; track c) {
                              <code class="me-1">{{ c }}</code>
                            }
                          </span>
                        }
                        @if (entry.destinationCountries?.length) {
                          <span class="me-3"
                            >Dest:
                            @for (c of entry.destinationCountries; track c) {
                              <code class="me-1">{{ c }}</code>
                            }
                          </span>
                        }
                        @if (entry.originLocations?.length) {
                          <span class="me-3"
                            >Origin locations:
                            @for (l of entry.originLocations; track l.id) {
                              <span class="badge bg-light text-dark border me-1"
                                >{{ l.name }} <small>#{{ l.id }}</small></span
                              >
                            }
                          </span>
                        }
                        @if (entry.destinationLocations?.length) {
                          <span class="me-3"
                            >Dest locations:
                            @for (l of entry.destinationLocations; track l.id) {
                              <span class="badge bg-light text-dark border me-1"
                                >{{ l.name }} <small>#{{ l.id }}</small></span
                              >
                            }
                          </span>
                        }
                      </div>
                    }
                    @if (entry.explanation?.length) {
                      <div
                        class="mb-2 p-2 rounded small"
                        style="background: #fffbf0; border: 1px solid #ffe58f"
                      >
                        <strong class="text-muted me-2">Explanation</strong>
                        @for (line of entry.explanation; track $index) {
                          <span
                            class="badge me-1 mb-1"
                            style="
                              background: #fff3cd;
                              color: #664d03;
                              border: 1px solid #ffe58f;
                            "
                            >{{ line }}</span
                          >
                        }
                      </div>
                    }
                    @if (entry.topCandidates?.length) {
                      <details>
                        <summary
                          class="small text-muted"
                          style="cursor: pointer"
                        >
                          Top candidates ({{ entry.topCandidates.length }})
                        </summary>
                        <table
                          class="table table-sm table-bordered table-striped mt-2 small"
                        >
                          <thead class="table-secondary">
                            <tr>
                              <th>ID</th>
                              <th>Name</th>
                              <th>Score</th>
                              <th>Margin</th>
                              <th>Type</th>
                              <th>Applicability</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (c of entry.topCandidates; track $index) {
                              <tr [class.table-warning]="$first">
                                <td>
                                  <code>{{ c.id ?? "—" }}</code>
                                </td>
                                <td>{{ c.name ?? "—" }}</td>
                                <td>
                                  <strong>{{ c.score ?? "—" }}</strong>
                                </td>
                                <td>{{ c.margin ?? "—" }}</td>
                                <td>{{ c.marginType ?? "—" }}</td>
                                <td>{{ c.applicability ?? "—" }}</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </details>
                    }
                  </div>
                  <!-- /card-body -->
                </div>
                <!-- /card -->
              }
            </div>
          </div>
        }
        <!-- /debugData -->
      </div>
      <!-- /debug-footer-body -->
    }
  </div>
  <!-- /debug-footer-panel -->
}
